import { useContext, useState } from "react";
import { LuSettings } from "react-icons/lu";
import { GrUserAdmin } from "react-icons/gr";
import { RiAdminLine, RiFeedbackLine } from "react-icons/ri";
import { IoMdLogOut } from "react-icons/io";
import styled from "styled-components";
import ButtonIcon from "../../interface/ButtonIcon";
import General from "./General";
import { UserContext } from "../../contexts/UserContext";
import Administration from "./Administration";
import CreateEditUserForm from "../authentication/CreateEditUserForm";

import { logout } from "../../services/apiAuth";
import {
  useAzureAuth,
  logoutFromAzure,
} from "../authentication/entra/useAzureAuth";

import MobileCreateEditUserForm from "../authentication/MobileCreateEditUserForm";
import ButtonRound from "../../interface/ButtonRound";
import MobileButtonRound from "../../mobile/interface/ButtonRound";

//#region styles

const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 50vh;
  max-height: 85vh;
  width: 85vw;
  margin-bottom: 4em;
  font-size: 0.75em;
  border-radius: 1rem;
  background-color: rgba(255, 255, 255, 0.13);
  backdrop-filter: blur(10px);
  padding: 3rem 2rem;
  box-shadow: var(--shadow-main);
`;

const MobileTabContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0.5em;
  width: 100%;
  border-radius: 1rem;
  background-color: var(--color-silver-100); // Example background color
  box-shadow: var(--shadow-btn);
`;

const MobileTab = styled.button`
  display: flex;
  padding: 1em;
  width: 100%;
  cursor: pointer;
  background-color: transparent;
  border: none;
  align-items: center;

  // Add more styles for the tabs
`;

const MobileButtonContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const Layout = styled.div`
  display: flex;
  height: 55rem;
  min-width: 60rem;
  width: auto;
  height: auto;
  border-radius: 1rem;
  background-color: rgba(255, 255, 255, 0.13);
  backdrop-filter: blur(10px);
  padding: 3rem 2rem;
  box-shadow: var(--shadow-main);
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;

  padding: 0.5em;
  min-width: 18rem;
  border-radius: 1rem;
  background-color: var(--color-silver-100); // Example background color
  box-shadow: var(--shadow-btn);
`;

const Tab = styled.button`
  padding: 10px;
  cursor: pointer;
  background-color: transparent;
  border: none;
  text-align: left;
  // Add more styles for the tabs
`;

const ContentArea = styled.div`
  padding: 1em;
  flex-grow: 1;
  // Additional styles for content area
`;

const ButtonContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: flex-start;
  align-self: flex-end;
`;

const TabIcon = styled.span`
  background: none;
  border: none;
  margin-right: 0.4em;
  border-radius: var(--border-radius-sm);
  transition: all 0.2s;

  &:hover {
    color: var(--color-grey-100);
  }
`;

const LogoutButton = styled.div`
  display: flex;
  //align-self: center;
`;

//#endregion

function SettingsForm() {
  const [activeTab, setActiveTab] = useState("tab1");

  const { userDetails } = useContext(UserContext);
  const { isMobile } = userDetails;
  const {
    userEmail,
    userToken,
    userName,
    userCompany,
    userAccessLevel,
    userImage,
    userWiMuleToken,
    userWiMuleTokenTimestamp,
  } = userDetails || {}; // Destructure from userDetails and provide a fallback

  const isAdmin =
    userDetails.userAccessLevel === "Admin" ||
    userDetails.userAccessLevel === "God";

  // Logout event handler
  const handleLogout = async () => {
    try {
      await logout();
      console.log("You have been logged out from FireBase successfully.");
      // Here you might want to redirect the user to the login page or perform other cleanup actions
    } catch (error) {
      console.error("Logout from firebase failed: ", error.message);
    }
    try {
      await logoutFromAzure();
    } catch (error) {
      console.error("Logout from Azure failed", error.message);
    }
  };

  return (
    <>
      {isMobile ? (
        <MobileLayout>
          <ContentArea>
            {activeTab === "tab1" && (
              <div>
                <h5>Application General</h5>
                <General />
              </div>
            )}

            {activeTab === "addUser" && (
              <div>
                <h5>Edit User</h5>
                <MobileCreateEditUserForm />
              </div>
            )}
          </ContentArea>
          <MobileTabContainer>
            <MobileTab onClick={() => setActiveTab("tab1")}>
              <TabIcon>
                <LuSettings />
              </TabIcon>
              General
            </MobileTab>

            <MobileTab
              disabled={!isAdmin}
              onClick={() => setActiveTab("addUser")}
            >
              <TabIcon>
                {(isAdmin && <GrUserAdmin />) || <RiAdminLine />}
              </TabIcon>
              Edit User
            </MobileTab>

            <MobileButtonContainer>
              <LogoutButton onClick={handleLogout}>
                <MobileButtonRound>
                  <IoMdLogOut />
                </MobileButtonRound>
              </LogoutButton>
            </MobileButtonContainer>
          </MobileTabContainer>
        </MobileLayout>
      ) : (
        <Layout>
          <TabContainer>
            <Tab onClick={() => setActiveTab("tab1")}>
              <TabIcon>
                <LuSettings />
              </TabIcon>
              General
            </Tab>
            <Tab disabled={true} onClick={() => setActiveTab("tab2")}>
              <TabIcon>
                {(isAdmin && <GrUserAdmin />) || <RiAdminLine />}
              </TabIcon>
              Administration
            </Tab>
            <Tab disabled={!isAdmin} onClick={() => setActiveTab("addUser")}>
              <TabIcon>
                {(isAdmin && <GrUserAdmin />) || <RiAdminLine />}
              </TabIcon>
              Add/Edit User
            </Tab>
            <Tab onClick={() => setActiveTab("tab3")}>
              <TabIcon>
                <RiFeedbackLine />
              </TabIcon>
              Feedback
            </Tab>
            <ButtonContainer>
              <LogoutButton onClick={handleLogout}>
                <ButtonRound>
                  <IoMdLogOut />
                </ButtonRound>
              </LogoutButton>
            </ButtonContainer>

            {/* Add more tabs as needed */}
          </TabContainer>

          <ContentArea>
            {activeTab === "tab1" && (
              <div>
                <h2>Application General</h2>
                <General />
              </div>
            )}
            {activeTab === "tab2" && (
              <div>
                <h2>Administration</h2>
                <Administration />
              </div>
            )}
            {activeTab === "tab3" && (
              <div>
                <h2>Feedback</h2>
                <p>
                  This is some content for the third tab. Add more details here.
                </p>
              </div>
            )}
            {activeTab === "addUser" && (
              <div>
                <h2>Add/Edit User</h2>
                <CreateEditUserForm />
              </div>
            )}
            {/* Conditionally render content for other tabs */}
          </ContentArea>
        </Layout>
      )}
    </>
  );
}

export default SettingsForm;
